import * as React from 'react';
import { useEffect, useMemo, useState } from 'react';
import type { FieldValues } from 'react-hook-form';
import { useForm, useWatch } from 'react-hook-form';
import { Link } from 'react-router-dom';
import type { InviteNewDispensaryForBankResponse, MinifiedDispensary } from '@gcv/shared';
import {
  GCD_RIVERBANK_BANK_ID,
  GcdUserRole,
  IndustryType,
  IndustryTypeFriendlyMap,
  NonCannabisIndustryTypes,
  OrganizationStage,
  PAYQWICK_BANK_ID
} from '@gcv/shared';
import { Box, Typography } from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';

import { BanksApi } from 'api';
import { USStateOptions } from 'domain/consts';
import { GcdRoleOptions, PayqwickRoleOptions } from 'domain/consts/roles';
import { knowledgeBasePath } from 'domain/consts/support';
import { useComponent } from 'hooks';
import { MINIFIED_DISPENSARIES_KEY } from 'hooks/keys';
import { useUser } from 'hooks/queries';
import { getFiBankStore } from 'stores/FiBankStore';
import { getFiDispensaryStore } from 'stores/FiDispensaryStore';
import { getFiRelationshipManagerStore } from 'stores/FiRelationshipManagerStore';
import { getSnackbarStore, SnackbarSeverity } from 'stores/SnackBarStore';
import { Button, Container, ContainerItem, Header, Hyperlink, Text } from 'ui';
import { InputCheckBox, InputEmail, InputSelect, InputText } from 'ui/molecules';
import { Dialog } from 'ui/organisms';
import { theme } from 'ui/theme';
import palette from 'ui/theme/palette';
import type { TemplateOption } from '../actions';
import { actions } from '../actions';
import { getRelationshipManagerOptions } from '../utils/getRelationshipManagerOptions';
import { ExistingDispensaries } from './existing-dispensaries';

export const InviteDispensaryButton: React.FC = useComponent((props) => {
  const fiDispensaryStore = getFiDispensaryStore();
  const relationshipManagerStore = getFiRelationshipManagerStore();
  const bankStore = getFiBankStore();
  const queryClient = useQueryClient();
  const { userBankId } = useUser();

  const canChooseIndustryType = !!getFiBankStore().bank.features?.IndustryType?.enabled;

  const [loading, setLoading] = useState(false);
  const [isInviteModalOpen, setIsInviteModalOpen] = useState(false);
  const [invalidDispensaryName, setInvalidDispensaryName] = useState(false);
  const [isPlantTouchingTemplate, setIsPlantTouchingTemplate] = useState(false);
  const [templateOptions, setTemplateOptions] = useState<TemplateOption[]>([]);
  const [skipUserInvite, setSkipUserInvite] = useState(false);

  const form = useForm({
    mode: 'onBlur',
    defaultValues: {} as FieldValues
  });

  const selectedIndustryType = useWatch({ control: form.control, name: 'industryType' }) as
    | IndustryType
    | undefined;

  const filteredTemplateOptions = useMemo(() => {
    if (selectedIndustryType && NonCannabisIndustryTypes.includes(selectedIndustryType)) {
      return templateOptions.filter((templateOption) => !templateOption.plant_touching);
    }
    return templateOptions;
  }, [selectedIndustryType, templateOptions]);

  useEffect(() => {
    setTemplateOptions(actions.getPublishedTemplates());
  }, []);

  useEffect(() => {
    if (selectedIndustryType === undefined || selectedIndustryType === IndustryType.CANNABIS) {
      return;
    }

    const selectedTemplate = templateOptions.find(
      (templateOption) => templateOption.value === form.getValues('dueDiligenceTemplate')
    );

    if (selectedTemplate?.plant_touching) {
      form.setValue('dueDiligenceTemplate', undefined, { shouldValidate: true });
      setIsPlantTouchingTemplate(false);
    }
  }, [selectedIndustryType, templateOptions]);

  useEffect(() => {
    if (form.formState.isSubmitSuccessful) {
      onClose();
    }
  }, [form.formState.isSubmitSuccessful]);

  const onSubmit = async (data: FieldValues) => {
    const bankApi = new BanksApi();

    setLoading(true);
    try {
      if (data.organization_stage === 'not-sure') {
        data.organization_stage = OrganizationStage.PENDING_LICENSE;
      }
      let response = await bankApi.addDispensaryToBank(bankStore.bank.id, {
        name: data.accountName.trim(),
        state: data.accountState,
        primaryContact: skipUserInvite
          ? undefined
          : {
              firstName: data.firstName.trim(),
              lastName: data.lastName.trim(),
              email: data.email.trim().toLowerCase(),
              pq_user_role: data.pq_user_role === 'none' ? undefined : data.pq_user_role,
              gcd_user_role: data.gcd_user_role === 'none' ? undefined : data.gcd_user_role
            },
        template_id: data.dueDiligenceTemplate,
        isInvitedByFi: true,
        relationshipManager: {
          assigned_user: bankStore.staff.find((s) => s.id === data.relationshipManager)?.id,
          assigned_group: bankStore.bank.groups.find((g) => g.id === data.relationshipManager)?.id
        },
        organization_stage: !isPlantTouchingTemplate ? OrganizationStage.ACTIVE : data.organization_stage,
        industryType: canChooseIndustryType ? data.industryType : IndustryType.CANNABIS
      });
      if ((response as { data: any }).data === null) {
        fiDispensaryStore.updateInvitedDispensaries();
        // Invalidate the cache to refetch the latest invited dispensaries
        queryClient.invalidateQueries({ queryKey: [MINIFIED_DISPENSARIES_KEY, userBankId] });
        actions.sortDispensaries(fiDispensaryStore.dispensaries);

        getSnackbarStore().showSnackbar(
          SnackbarSeverity.Warning,
          `An account for this user already exists in Green Check. On their next login, we'll automatically ask them to use the existing account or create a new one.`
        );
      } else {
        response = response as InviteNewDispensaryForBankResponse;
        bankStore.updateBank(response.bank);
        fiDispensaryStore.addDispensary(response.minifiedDispensary);
        relationshipManagerStore.saveRelationshipManager(response.dispensary.id, data.relationshipManager);

        // Update the TanStack Query cache with the new dispensary
        queryClient.setQueryData(
          [MINIFIED_DISPENSARIES_KEY, userBankId],
          (oldData: MinifiedDispensary[] | undefined) => {
            if (oldData === undefined) return oldData;
            return [...oldData, (response as InviteNewDispensaryForBankResponse).minifiedDispensary];
          }
        );
        actions.sortDispensaries(fiDispensaryStore.dispensaries);

        getSnackbarStore().showSnackbar(
          SnackbarSeverity.Success,
          `${data.accountName} has been invited successfully.`
        );
      }
    } catch (e: any) {
      if (
        e.response &&
        e.response.errors.length &&
        e.response.errors[0] ===
          'This invitation cannot be sent as this account is currently pending CRB confirmation.'
      ) {
        getSnackbarStore().showSnackbar(
          SnackbarSeverity.Error,
          'This invitation cannot be sent as this email account currently has a pending invitation.'
        );
      } else {
        getSnackbarStore().showSnackbar(SnackbarSeverity.Error, 'There was an issue inviting the account.');
      }
    } finally {
      setLoading(false);
    }
  };

  const relationshipManagerOptions = getRelationshipManagerOptions();

  const onClose = () => {
    setIsInviteModalOpen(false);
    setSkipUserInvite(false);
    form.reset({});
  };

  return (
    <>
      <Button
        headerComponent={true}
        label={'Invite Account'}
        style={{
          padding: '10px 20px',
          fontSize: '14px',
          fontWeight: '400',
          lineHeight: '15px',
          width: 'max-content'
        }}
        color="header-button"
        onClick={() => setIsInviteModalOpen(true)}
        dataCy="invite-account-button"
      />

      <Dialog
        isOpen={isInviteModalOpen}
        handleClose={onClose}
        title={'Invite Account'}
        dataCy={'invite-account-modal'}
        action={
          <Button
            label={skipUserInvite ? 'Add Account' : 'Invite Account'}
            isLoading={loading}
            color="primary"
            onClick={() => {
              if (!invalidDispensaryName) {
                form.handleSubmit(onSubmit)();
              }
            }}
            dataCy="submit-button"
          />
        }
        width="459px"
      >
        <Container width="100%" padding="0">
          <ContainerItem width="100%" margin="10px 0" padding="0">
            <InputText
              name="accountName"
              label="Account Name"
              rules={{
                required: { message: 'is required', value: true },
                maxLength: { message: 'is too long', value: 50 }
              }}
              dataCy="legal-name"
              {...form}
            />
            <ExistingDispensaries
              form={form}
              handleInvalidDispensaryName={(value) => {
                setInvalidDispensaryName(value);
              }}
            />
          </ContainerItem>

          <ContainerItem width="100%" margin="10px 0" padding="0">
            <InputSelect
              name="accountState"
              label="State"
              options={USStateOptions}
              rules={{ required: { message: 'is required', value: true } }}
              dataCy="state"
              {...form}
            />
          </ContainerItem>

          {canChooseIndustryType && (
            <ContainerItem width="100%" margin="10px 0" padding="0">
              <InputSelect
                name="industryType"
                label="Industry Type"
                options={Object.values(IndustryType).map((industryType) => {
                  return { label: IndustryTypeFriendlyMap[industryType], value: industryType };
                })}
                rules={{ required: { message: 'is required', value: true } }}
                dataCy="industryType"
                {...form}
              />
            </ContainerItem>
          )}

          <ContainerItem width="100%" margin="10px 0" padding="0">
            <InputSelect
              name="dueDiligenceTemplate"
              label="Due Diligence Template"
              options={filteredTemplateOptions}
              addTag="Marijuana-Licensed"
              rules={{ required: { message: 'is required', value: true } }}
              additionalOnChange={(selected) => {
                const templateOption = filteredTemplateOptions.find((o) => o.value === selected.target.value);
                if (templateOption?.plant_touching) {
                  setIsPlantTouchingTemplate(true);
                } else {
                  setIsPlantTouchingTemplate(false);
                }
              }}
              {...form}
            />
          </ContainerItem>

          <ContainerItem flex width="100%" margin="0 0 10px 0" padding="0">
            <Typography
              sx={{
                '& a': {
                  color: theme.palette.primary.main,
                  textDecoration: 'underline',
                  '&:hover': {
                    cursor: 'pointer',
                    color: '#1270FC'
                  }
                }
              }}
              component="div"
              variant="body2"
            >
              To add additional templates, go to the{' '}
              <Link
                id="link"
                to="/secure/fi/settings/templates?tab=due-diligence"
                target="_blank"
                rel="noopener noreferrer"
              >
                Due Diligence tab in Settings.
              </Link>
            </Typography>
          </ContainerItem>

          {isPlantTouchingTemplate && (
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: 1,
                width: '100%',
                padding: 2,
                marginY: 1,
                borderRadius: 2,
                backgroundColor: 'rgba(3, 166, 91, 0.07)', // TODO use color from theme when GROW-253 is merged in
                border: 1,
                borderColor: palette.primary.border
              }}
            >
              <InputSelect
                name="organization_stage"
                label="Business Stage"
                defaultValue={OrganizationStage.ACTIVE}
                options={[
                  { label: 'Active (fully licensed & operating)', value: OrganizationStage.ACTIVE },
                  { label: 'Seedling (new or pre-operational)', value: OrganizationStage.PENDING_LICENSE },
                  { label: 'Not sure', value: 'not-sure' }
                ]}
                rules={{ required: { message: 'is required', value: true } }}
                {...form}
              />
              <Text
                content={
                  'Selecting "Seedling" or "Not sure" will show additional questions during onboarding. This will accurately set the business stage to help understand their operating status.'
                }
                type="body3"
              />
            </Box>
          )}

          <ContainerItem width="100%" margin="10px 0" padding="0">
            <InputSelect
              name="relationshipManager"
              label="Relationship Manager"
              defaultValue={'none'}
              options={relationshipManagerOptions}
              {...form}
            />
          </ContainerItem>

          <ContainerItem flex width="100%" margin="0 0 10px 0" padding="0">
            <Text
              content="The relationship manager is the main point of contact for the account."
              type="body3"
            />
          </ContainerItem>

          <ContainerItem flex width="100%" margin="20px 0 0 0" padding="0">
            <InputCheckBox
              name="skipUserInvite"
              label="Add account without inviting a user"
              control={form.control}
              checked={skipUserInvite}
              onChange={(_, checked) => setSkipUserInvite(checked)}
            />
          </ContainerItem>

          {skipUserInvite && (
            <ContainerItem width="100%" margin="4px 0 10px 0" padding="0">
              <Text content="You can invite a user anytime from the account page." type="body3" />
            </ContainerItem>
          )}

          {!skipUserInvite && (
            <>
              <ContainerItem width="100%" margin="10px 0" padding="0">
                <Header type="h4" content="Account Owner Information" />
              </ContainerItem>

              <ContainerItem width="50%" margin="10px 0" padding="0 10px 0 0">
                <InputText
                  name="firstName"
                  label="First Name"
                  rules={{ required: { message: 'is required', value: true } }}
                  dataCy="first-name"
                  {...form}
                />
              </ContainerItem>

              <ContainerItem width="50%" margin="10px 0" padding="0 0 0 10px">
                <InputText
                  name="lastName"
                  label="Last Name"
                  rules={{ required: { message: 'is required', value: true } }}
                  dataCy="last-name"
                  {...form}
                />
              </ContainerItem>

              <ContainerItem width="100%" margin="10px 0" padding="0">
                <InputEmail
                  name="email"
                  label="Email Address"
                  rules={{ required: { message: 'is required', value: true } }}
                  dataCy="email"
                  {...form}
                />
              </ContainerItem>

              <ContainerItem width="100%" margin="10px 0" padding="0">
                <Text
                  content="The account owner will receive an email inviting them to complete an application in Green Check Verified."
                  type="body2"
                />
              </ContainerItem>

              {getFiBankStore().bank.id === PAYQWICK_BANK_ID ? (
                <>
                  <ContainerItem width="100%" margin="0.75rem 0" padding="0">
                    <Text sx={{ fontWeight: 'bold' }} content="Permission to access the PayQwick system" />
                  </ContainerItem>

                  <ContainerItem width="100%" padding="0">
                    <Text inline content="Learn more about PayQwick user roles and permissions in this " />
                    <Hyperlink
                      label="support article."
                      newTab
                      url={`${knowledgeBasePath}/payqwick-user-access-comparison-chart`}
                    />
                  </ContainerItem>

                  <ContainerItem width="100%" margin="0.75rem 0" padding="0">
                    <InputSelect
                      {...form}
                      label="Select Role"
                      name="pq_user_role"
                      options={PayqwickRoleOptions}
                      rules={{
                        required: { message: 'is required', value: true }
                      }}
                      defaultValue={0}
                      dataCy="pq_user_role"
                    />
                  </ContainerItem>
                </>
              ) : null}
              {getFiBankStore().bank.id === GCD_RIVERBANK_BANK_ID ? (
                <>
                  <ContainerItem width="100%" margin="0.75rem 0" padding="0">
                    <Text
                      sx={{ fontWeight: 'bold' }}
                      content="Permission to access the Green Check Direct system"
                    />
                  </ContainerItem>

                  <ContainerItem width="100%" padding="0">
                    <Text
                      inline
                      content="Learn more about Green Check Direct user roles and permissions in this "
                    />
                    <Hyperlink
                      label="support article."
                      newTab
                      url={`${knowledgeBasePath}/gcd/green-check-direct-user-access-comparison-chart`}
                    />
                  </ContainerItem>

                  <ContainerItem width="100%" margin="0.75rem 0" padding="0">
                    <InputSelect
                      {...form}
                      label="Select Role"
                      name="gcd_user_role"
                      options={GcdRoleOptions}
                      rules={{
                        required: { message: 'is required', value: true }
                      }}
                      defaultValue={GcdUserRole.Admin}
                      dataCy="gcd_user_role"
                    />
                  </ContainerItem>
                </>
              ) : null}
            </>
          )}
        </Container>
      </Dialog>
    </>
  );
});
